/**
 * ErvaPOS - Inventory Page
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Package,
  Loader2,
  Search,
  QrCode,
  Undo2,
  CheckCircle,
  XCircle,
  Play,
  Euro,
  AlertTriangle,
  BarChart3,
  ArrowUpDown,
  Plus,
  Trash2,
  ClipboardList,
  ScanLine,
  CalendarDays,
  Smartphone,
} from 'lucide-react';
import { inventoryAPI } from '../services/api';
import { toast } from 'sonner';

const SORTABLE_COLUMNS = {
  product_name: 'product_name',
  category: 'category',
  total_stock: 'total_stock',
  cost_price: 'cost_price',
  sell_price: 'sell_price',
  sell_value: 'sell_value',
};

function formatDateInput(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getInitialExpiryLimits() {
  const today = new Date();
  const minimum = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const maximum = new Date(today.getFullYear() + 10, today.getMonth(), today.getDate());
  return { min: formatDateInput(minimum), max: formatDateInput(maximum) };
}

export const InventoryPage = () => {
  const expiryLimits = useMemo(getInitialExpiryLimits, []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stockOverview, setStockOverview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('product_name');
  const [sortDirection, setSortDirection] = useState('asc');

  const [activeSession, setActiveSession] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [lastScan, setLastScan] = useState(null);
  const scanInputRef = useRef(null);

  // Stock inicial
  const [inventoryMode, setInventoryMode] = useState('normal'); // normal | initial
  const [initialSessionId, setInitialSessionId] = useState(null);
  const [initialSessionStatus, setInitialSessionStatus] = useState(null);
  const [showInitialStartModal, setShowInitialStartModal] = useState(false);
  const [showInitialFinalizeModal, setShowInitialFinalizeModal] = useState(false);
  const [initialSessionName, setInitialSessionName] = useState('');
  const [initialSessionNotes, setInitialSessionNotes] = useState('');
  const [initialScanInput, setInitialScanInput] = useState('');
  const [initialResolvedProduct, setInitialResolvedProduct] = useState(null);
  const [initialEntryRows, setInitialEntryRows] = useState([
    { quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }
  ]);
  const [initialLastSave, setInitialLastSave] = useState(null);
  const [initialSessionsHistory, setInitialSessionsHistory] = useState([]);
  const [normalSessionsHistory, setNormalSessionsHistory] = useState([]);
  const [historyTypeFilter, setHistoryTypeFilter] = useState('all');
  const [historySessionFilter, setHistorySessionFilter] = useState('all');
  const [historyProductFilter, setHistoryProductFilter] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [showHistorySessionModal, setShowHistorySessionModal] = useState(false);
  const initialScanInputRef = useRef(null);



  const loadStockOverview = async () => {
    try {
      setLoading(true);
      const response = await inventoryAPI.getStockOverview();
      setStockOverview(response.data);
    } catch (error) {
      toast.error('Erro ao carregar stock');
    } finally {
      setLoading(false);
    }
  };

  const loadSessionStatus = useCallback(async () => {
    if (!activeSession) return;
    try {
      const response = await inventoryAPI.getSessionStatus(activeSession);
      setSessionStatus(response.data);
    } catch (error) {
      toast.error('Erro ao carregar sessão');
    }
  }, [activeSession]);

  const loadInventoryHistory = useCallback(async () => {
    try {
      const [initialRes, normalRes] = await Promise.all([
        inventoryAPI.getInitialSessions().catch(() => ({ data: [] })),
        inventoryAPI.getSessions().catch(() => ({ data: [] })),
      ]);

      setInitialSessionsHistory(Array.isArray(initialRes?.data) ? initialRes.data : []);
      setNormalSessionsHistory(Array.isArray(normalRes?.data) ? normalRes.data : []);
    } catch (error) {
      toast.error('Erro ao carregar histórico de inventário');
    }
  }, []);

  useEffect(() => {
    loadStockOverview();
    loadInventoryHistory();
  }, [loadInventoryHistory]);

  useEffect(() => {
    if (activeSession) {
      loadSessionStatus();
      scanInputRef.current?.focus();
    }
  }, [activeSession, loadSessionStatus]);

  const startCountingSession = async () => {
    if (!sessionName.trim()) {
      toast.error('Insira um nome para a sessão');
      return;
    }

    try {
      const response = await inventoryAPI.startCounting(sessionName);
      setActiveSession(response.data.session_id);
      setShowStartModal(false);
      setSessionName('');
      toast.success('Sessão de contagem iniciada');
    } catch (error) {
      toast.error('Erro ao iniciar sessão');
    }
  };

  const handleScan = async (e) => {
    if (e.key !== 'Enter' || !scanInput.trim()) return;

    try {
      const response = await inventoryAPI.scanItemV2(activeSession, scanInput.trim());

      if (response.data.success) {
        setLastScan({
          success: true,
          product: response.data.product_name,
          count: response.data.current_count,
          lot: response.data.lot_number,
          expiry: response.data.expiry_date,
        });
        toast.success(`${response.data.product_name} - ${response.data.current_count} un`);
      } else {
        setLastScan({
          success: false,
          message: response.data.message
        });
        toast.error(response.data.message);
      }

      setScanInput('');
      loadSessionStatus();
    } catch (error) {
      toast.error('Erro ao registar scan');
      setScanInput('');
    }
  };

  const undoLastScan = async () => {
    try {
      const response = await inventoryAPI.undoLastScan(activeSession);
      toast.success(`Anulado: ${response.data.product}`);
      setLastScan(null);
      loadSessionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anular');
    }
  };

  const finalizeSession = async (applyAdjustments, isPartial) => {
    try {
      const response = await inventoryAPI.finalizeSession(activeSession, applyAdjustments, isPartial);

      toast.success('Inventário finalizado');
      setShowFinalizeModal(false);
      setActiveSession(null);
      setSessionStatus(null);
      setLastScan(null);
      await loadStockOverview();
      await loadInventoryHistory();

      if (response.data.discrepancies?.length > 0) {
        toast.warning(`${response.data.discrepancies.length} discrepâncias encontradas`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar inventário');
    }
  };

  const resetInitialEntryRows = () => {
    setInitialEntryRows([{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }]);
  };

  const loadInitialSessionStatus = useCallback(async (sessionIdParam = null) => {
    const sid = sessionIdParam || initialSessionId;
    if (!sid) return;

    try {
      const response = await inventoryAPI.getInitialSessionStatus(sid);
      setInitialSessionStatus(response.data || null);
    } catch (error) {
      toast.error('Erro ao carregar stock inicial');
    }
  }, [initialSessionId]);

  useEffect(() => {
    if (inventoryMode === 'initial' && initialSessionId) {
      loadInitialSessionStatus(initialSessionId);
      initialScanInputRef.current?.focus();
    }
  }, [inventoryMode, initialSessionId, loadInitialSessionStatus]);

  const startInitialInventorySession = async () => {
    if (!initialSessionName.trim()) {
      toast.error('Indique um nome para a sessão inicial');
      return;
    }

    try {
      const response = await inventoryAPI.startInitialSession(
        initialSessionName.trim(),
        initialSessionNotes.trim() || null
      );

      const sid = response?.data?.session_id;
      setInventoryMode('initial');
      setInitialSessionId(sid || null);
      setShowInitialStartModal(false);
      setInitialResolvedProduct(null);
      setInitialLastSave(null);
      resetInitialEntryRows();
      setInitialSessionName('');
      setInitialSessionNotes('');

      if (sid) {
        await loadInitialSessionStatus(sid);
      }

      toast.success('Sessão de stock inicial iniciada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar stock inicial');
    }
  };

  const resolveInitialProductByScan = async () => {
    if (!initialSessionId) {
      toast.error('Inicie primeiro a sessão de stock inicial');
      return;
    }

    const code = String(initialScanInput || '').trim();
    if (!code) {
      toast.error('Leia ou introduza um código');
      return;
    }

    try {
      const response = await inventoryAPI.resolveInitialProduct(initialSessionId, code);
      const data = response?.data || {};
      const product = data?.product || {};

      const normalizedProduct = {
        product_id: product?.id || data?.product_id || '',
        product_name: product?.name || data?.product_name || '',
        barcode: product?.barcode || data?.barcode || '',
        reference: product?.reference || data?.reference || '',
        category: product?.category || data?.category || 'Sem categoria',
        brand: product?.brand || data?.brand || '',
        cost_price: Number(product?.cost_price ?? data?.cost_price ?? 0),
        sell_price: Number(product?.sell_price ?? data?.sell_price ?? 0),
        current_total_stock: Number(product?.current_total_stock ?? data?.current_total_stock ?? 0),
        matched_batch: data?.matched_batch || null,
        existing_rows: Array.isArray(data?.existing_rows) ? data.existing_rows : [],
      };

      setInitialResolvedProduct(normalizedProduct);

      if (normalizedProduct.existing_rows.length > 0) {
        setInitialEntryRows(
          normalizedProduct.existing_rows.map((row) => ({
            quantity: Number(row?.quantity || 1),
            expiry_date: String(row?.expiry_date || ''),
            lot_number: String(row?.lot_number || ''),
            no_expiry: !!row?.no_expiry || !String(row?.expiry_date || '').trim(),
          }))
        );
      } else {
        resetInitialEntryRows();
      }

      setInitialScanInput('');
      initialScanInputRef.current?.focus();
      toast.success(`Artigo encontrado: ${normalizedProduct.product_name || 'OK'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Artigo não encontrado');
    }
  };

  const handleInitialScanKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    await resolveInitialProductByScan();
  };

  const updateInitialEntryRow = (index, field, value) => {
    setInitialEntryRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== index) return row;

        const next = { ...row, [field]: value };

        if (field === 'no_expiry' && value === true) {
          next.expiry_date = '';
        }

        return next;
      })
    );
  };

  const addInitialEntryRow = () => {
    setInitialEntryRows((prev) => [
      ...prev,
      { quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }
    ]);
  };

  const removeInitialEntryRow = (index) => {
    setInitialEntryRows((prev) => {
      if (prev.length <= 1) {
        return [{ quantity: 1, expiry_date: '', lot_number: '', no_expiry: false }];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

    const saveInitialResolvedProduct = async () => {
      if (!initialSessionId) {
        toast.error('Sessão inicial não encontrada');
        return;
      }

      const productId = String(initialResolvedProduct?.product_id || '').trim();
      if (!productId) {
        toast.error('Leia primeiro um artigo');
        return;
      }

      const category = String(initialResolvedProduct?.category || '').trim() || 'Sem categoria';
      const brand = String(initialResolvedProduct?.brand || '').trim();
      const costPrice = Number(initialResolvedProduct?.cost_price || 0);
      const sellPrice = Number(initialResolvedProduct?.sell_price || 0);

      const rows = initialEntryRows.map((row) => ({
        quantity: Number(row.quantity || 0),
        expiry_date: row.no_expiry ? '' : String(row.expiry_date || '').trim(),
        lot_number: String(row.lot_number || '').trim(),
        no_expiry: !!row.no_expiry,
        cost_price: costPrice,
        sell_price: sellPrice,
      }));

      try {
        const response = await inventoryAPI.saveInitialEntry(initialSessionId, {
          product_id: productId,
          category,
          brand,
          cost_price: costPrice,
          sell_price: sellPrice,
          rows,
        });

        setInitialLastSave(response?.data || null);
        setInitialResolvedProduct(null);
        resetInitialEntryRows();
        await loadInitialSessionStatus(initialSessionId);
        await loadInventoryHistory();
        initialScanInputRef.current?.focus();
        toast.success('Artigo gravado no stock inicial');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao gravar artigo');
      }
    };

  const finalizeInitialInventorySession = async (replaceExistingProductBatches = true) => {
    if (!initialSessionId) {
      toast.error('Sessão inicial não encontrada');
      return;
    }

    try {
      const response = await inventoryAPI.finalizeInitialSession(initialSessionId, replaceExistingProductBatches);

      toast.success('Stock inicial aplicado com sucesso');
      setShowInitialFinalizeModal(false);
      setInitialSessionId(null);
      setInitialSessionStatus(null);
      setInitialResolvedProduct(null);
      setInitialLastSave(response?.data || null);
      resetInitialEntryRows();
      setInventoryMode('normal');
      await loadStockOverview();
      await loadInventoryHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar stock inicial');
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDirection(column === 'product_name' || column === 'category' ? 'asc' : 'desc');
  };

  const categories = useMemo(() => {
    const cats = (stockOverview?.summary?.categories_summary || []).map((c) => c.category).filter(Boolean);
    return ['all', ...cats];
  }, [stockOverview]);

  const filteredItems = useMemo(() => {
    let items = [...(stockOverview?.items || [])];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) =>
        item.product_name?.toLowerCase().includes(q) ||
        item.barcode?.includes(searchQuery) ||
        item.category?.toLowerCase().includes(q) ||
        item.reference?.toLowerCase().includes(q) ||
        item.brand?.toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'all') {
      items = items.filter((item) => item.category === categoryFilter);
    }

    items.sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      const av = a?.[sortBy];
      const bv = b?.[sortBy];

      if (typeof av === 'number' || typeof bv === 'number') {
        return ((Number(av) || 0) - (Number(bv) || 0)) * direction;
      }

      return String(av || '').localeCompare(String(bv || ''), 'pt', { sensitivity: 'base' }) * direction;
    });

    return items;
  }, [stockOverview, searchQuery, categoryFilter, sortBy, sortDirection]);

  const inventorySessionSummaries = useMemo(() => {
    const initial = (initialSessionsHistory || []).map((session) => {
      const entries = Array.isArray(session.entries) ? session.entries : [];
      const totalValue = entries.reduce((acc, entry) => {
        const rows = Array.isArray(entry?.rows) ? entry.rows : [];
        return acc + rows.reduce((sum, row) => {
          return sum + (Number(row?.quantity || 0) * Number(row?.sell_price || entry?.sell_price || 0));
        }, 0);
      }, 0);

      return {
        id: session.session_id || session.id,
        raw_id: session.session_id || session.id,
        type: 'initial',
        type_label: 'Stock inicial',
        name: session.name || 'Stock inicial',
        status: session.status || 'unknown',
        created_at: session.created_at || '',
        completed_at: session.completed_at || '',
        products_counted: Number(session.products_counted || 0),
        rows_counted: Number(session.rows_counted || 0),
        total_units: Number(session.total_units || 0),
        total_value: Number(totalValue || 0),
        entries: entries,
        counts: [],
      };
    });

    const normal = (normalSessionsHistory || []).map((session) => {
      const counts = Array.isArray(session.counts) ? session.counts : [];
      const rowsCounted = counts.reduce((acc, item) => {
        const batches = Array.isArray(item?.batches_counted) ? item.batches_counted : [];
        return acc + (batches.length > 0 ? batches.length : 1);
      }, 0);
      const totalUnits = counts.reduce((acc, item) => acc + Number(item?.counted_quantity || 0), 0);
      const totalValue = counts.reduce((acc, item) => acc + Number(item?.sell_value || 0), 0);

      return {
        id: session.id || session.session_id,
        raw_id: session.id || session.session_id,
        type: 'normal',
        type_label: 'Contagem',
        name: session.name || 'Contagem normal',
        status: session.status || 'unknown',
        created_at: session.created_at || '',
        completed_at: session.completed_at || '',
        products_counted: Number(counts.length || session.total_products || session.products_counted || 0),
        rows_counted: Number(rowsCounted || 0),
        total_units: Number(totalUnits || session.total_counted || 0),
        total_value: Number(totalValue || session.total_sell_value || 0),
        entries: [],
        counts: counts,
      };
    });

    return [...initial, ...normal].sort((a, b) =>
      String(b.completed_at || b.created_at || '').localeCompare(String(a.completed_at || a.created_at || ''))
    );
  }, [initialSessionsHistory, normalSessionsHistory]);

  const inventoryHistoryLines = useMemo(() => {
    const initialLines = [];

    (initialSessionsHistory || []).forEach((session) => {
      (session.entries || []).forEach((entry) => {
        (entry.rows || []).forEach((row, idx) => {
          initialLines.push({
            key: `initial-${session.session_id || session.id}-${entry.entry_id || entry.product_id}-${idx}` ,
            session_id: session.session_id || session.id,
            session_name: session.name || 'Stock inicial',
            session_type: 'initial',
            session_type_label: 'Stock inicial',
            session_status: session.status || '',
            created_at: session.completed_at || session.created_at || '',
            product_id: entry.product_id || '',
            product_name: entry.product_name || '',
            barcode: entry.barcode || '',
            category: entry.category || 'Sem categoria',
            quantity: Number(row.quantity || 0),
            lot_number: row.lot_number || '',
            expiry_date: row.no_expiry ? 'Sem validade' : (row.expiry_date || ''),
            cost_price: Number(row.cost_price || entry.cost_price || 0),
            sell_price: Number(row.sell_price || entry.sell_price || 0),
          });
        });
      });
    });

    const normalLines = [];

    (normalSessionsHistory || []).forEach((session) => {
      (session.counts || []).forEach((row, idx) => {
        const batches = Array.isArray(row.batches_counted) ? row.batches_counted : [];

        if (batches.length > 0) {
          batches.forEach((batch, batchIdx) => {
            normalLines.push({
              key: `normal-${session.id || session.session_id}-${row.product_id || idx}-${batch.batch_id || batchIdx}` ,
              session_id: session.id || session.session_id,
              session_name: session.name || 'Contagem normal',
              session_type: 'normal',
              session_type_label: 'Contagem',
              session_status: session.status || '',
              created_at: session.completed_at || session.created_at || '',
              product_id: row.product_id || '',
              product_name: row.product_name || '',
              barcode: row.barcode || '',
              category: row.category || 'Sem categoria',
              quantity: Number(batch.counted_quantity || 0),
              lot_number: batch.lot_number || '',
              expiry_date: batch.expiry_date || '',
              cost_price: Number(row.cost_price || 0),
              sell_price: Number(row.sell_price || 0),
            });
          });
        } else {
          normalLines.push({
            key: `normal-${session.id || session.session_id}-${row.product_id || idx}` ,
            session_id: session.id || session.session_id,
            session_name: session.name || 'Contagem normal',
            session_type: 'normal',
            session_type_label: 'Contagem',
            session_status: session.status || '',
            created_at: session.completed_at || session.created_at || '',
            product_id: row.product_id || '',
            product_name: row.product_name || '',
            barcode: row.barcode || '',
            category: row.category || 'Sem categoria',
            quantity: Number(row.counted_quantity || 0),
            lot_number: '',
            expiry_date: '',
            cost_price: Number(row.cost_price || 0),
            sell_price: Number(row.sell_price || 0),
          });
        }
      });
    });

    return [...initialLines, ...normalLines].sort((a, b) =>
      String(b.created_at || '').localeCompare(String(a.created_at || ''))
    );
  }, [initialSessionsHistory, normalSessionsHistory]);

  const filteredInventorySessionSummaries = useMemo(() => {
    return inventorySessionSummaries.filter((session) => {
      if (historyTypeFilter !== 'all' && session.type !== historyTypeFilter) return false;
      if (historySessionFilter !== 'all' && session.id !== historySessionFilter) return false;

      const sessionDate = String(session.completed_at || session.created_at || '').slice(0, 10);
      if (historyDateFrom && sessionDate && sessionDate < historyDateFrom) return false;
      if (historyDateTo && sessionDate && sessionDate > historyDateTo) return false;

      if (historyProductFilter.trim()) {
        const q = historyProductFilter.trim().toLowerCase();
        const hasMatchInInitial = (session.entries || []).some((entry) =>
          String(entry?.product_name || '').toLowerCase().includes(q) ||
          String(entry?.barcode || '').toLowerCase().includes(q)
        );
        const hasMatchInNormal = (session.counts || []).some((entry) =>
          String(entry?.product_name || '').toLowerCase().includes(q) ||
          String(entry?.barcode || '').toLowerCase().includes(q)
        );
        if (!hasMatchInInitial && !hasMatchInNormal) return false;
      }

      return true;
    });
  }, [inventorySessionSummaries, historyTypeFilter, historySessionFilter, historyDateFrom, historyDateTo, historyProductFilter]);

  const filteredInventoryHistoryLines = useMemo(() => {
    return inventoryHistoryLines.filter((row) => {
      if (historyTypeFilter !== 'all' && row.session_type !== historyTypeFilter) return false;
      if (historySessionFilter !== 'all' && row.session_id !== historySessionFilter) return false;

      const rowDate = String(row.created_at || '').slice(0, 10);
      if (historyDateFrom && rowDate && rowDate < historyDateFrom) return false;
      if (historyDateTo && rowDate && rowDate > historyDateTo) return false;

      if (historyProductFilter.trim()) {
        const q = historyProductFilter.trim().toLowerCase();
        const hay = [row.product_name, row.barcode, row.category, row.lot_number].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [inventoryHistoryLines, historyTypeFilter, historySessionFilter, historyDateFrom, historyDateTo, historyProductFilter]);

  const historySessionOptions = useMemo(() => {
    const seen = new Set();
    return inventorySessionSummaries
      .map((session) => ({
        id: session.id,
        label: `${session.name} • ${session.type_label}`
      }))
      .filter((option) => {
        if (!option.id || seen.has(option.id)) return false;
        seen.add(option.id);
        return true;
      })
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'pt', { sensitivity: 'base' }));
  }, [inventorySessionSummaries]);

  const selectedHistorySessionDetail = useMemo(() => {
    if (!selectedHistorySession) return null;
    return inventorySessionSummaries.find((session) => session.id === selectedHistorySession) || null;
  }, [inventorySessionSummaries, selectedHistorySession]);

  const clearHistoryFilters = () => {
    setHistoryTypeFilter('all');
    setHistorySessionFilter('all');
    setHistoryProductFilter('');
    setHistoryDateFrom('');
    setHistoryDateTo('');
  };

  const resumeHistorySession = async (session) => {
    if (!session || session.status !== 'in_progress') return;

    try {
      setShowHistorySessionModal(false);

      if (session.type === 'normal') {
        setInventoryMode('normal');
        setActiveSession(session.id);
        setSessionStatus(null);
        setLastScan(null);

        const response = await inventoryAPI.getSessionStatus(session.id);
        setSessionStatus(response.data);

        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 0);

        toast.success('Sessão de contagem retomada');
        return;
      }

      if (session.type === 'initial') {
        setInventoryMode('initial');
        setInitialSessionId(session.id);
        setInitialSessionStatus(null);
        setInitialResolvedProduct(null);
        setInitialLastSave(null);
        resetInitialEntryRows();

        await loadInitialSessionStatus(session.id);

        setTimeout(() => {
          initialScanInputRef.current?.focus();
        }, 0);

        toast.success('Sessão de stock inicial retomada');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao retomar sessão');
    }
  };

  const inventoryStatusLabel = (status) => {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'completed') return 'Finalizado';
    if (value === 'in_progress') return 'Em progresso';
    if (value === 'partial') return 'Parcial';
    return status || '-';
  };

  const selectedHistorySessionLines = useMemo(() => {
    if (!selectedHistorySessionDetail) return [];
    return inventoryHistoryLines.filter((row) => row.session_id === selectedHistorySessionDetail.id);
  }, [inventoryHistoryLines, selectedHistorySessionDetail]);

  const filteredHistoryTotals = useMemo(() => {
    return filteredInventoryHistoryLines.reduce((acc, row) => {
      acc.rows += 1;
      acc.units += Number(row.quantity || 0);
      acc.sell_value += Number(row.quantity || 0) * Number(row.sell_price || 0);
      return acc;
    }, { rows: 0, units: 0, sell_value: 0 });
  }, [filteredInventoryHistoryLines]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (activeSession) {
    return (
      <div className="h-[calc(100vh-120px)] flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white font-['Manrope']">
              Contagem de Inventário
            </h1>
            <p className="text-slate-400">
              Sessão: {sessionStatus?.name || 'Em progresso'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={undoLastScan}
              disabled={!sessionStatus?.scan_history?.length}
              className="border-amber-500/50 text-amber-400"
              data-testid="undo-scan-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              Anular Último
            </Button>
            <Button
              onClick={() => setShowFinalizeModal(true)}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="finalize-inventory-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Finalizar
            </Button>
          </div>
        </div>

        <Card className="card-dark p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <QrCode className="w-8 h-8 text-emerald-400" />
            </div>
            <div className="flex-1">
              <Label className="text-slate-300 mb-2 block">
                Leia o código de barras ou QR Code
              </Label>
              <Input
                ref={scanInputRef}
                type="text"
                placeholder="Aguardando leitura..."
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={handleScan}
                className="bg-slate-800 border-slate-700 text-xl h-14"
                autoFocus
                data-testid="scan-input"
              />
            </div>
          </div>

          {lastScan && (
            <div className={`mt-4 p-4 rounded-lg ${lastScan.success ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
              {lastScan.success ? (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                  <div>
                    <p className="font-medium text-white">{lastScan.product}</p>
                    <p className="text-sm text-emerald-400">
                      Contagem atual: {lastScan.count} unidades
                      {lastScan.lot ? ` | Lote: ${lastScan.lot}` : ''}
                      {lastScan.expiry ? ` | Validade: ${lastScan.expiry}` : ''}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-rose-400" />
                  <p className="text-rose-400">{lastScan.message}</p>
                </div>
              )}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-4 gap-4">
          <Card className="card-dark p-4">
            <p className="text-sm text-slate-400">Produtos Contados</p>
            <p className="text-2xl font-bold text-white">
              {sessionStatus?.products_counted || 0}
            </p>
          </Card>
          <Card className="card-dark p-4">
            <p className="text-sm text-slate-400">Unidades Contadas</p>
            <p className="text-2xl font-bold text-emerald-400">
              {sessionStatus?.total_items_counted || 0}
            </p>
          </Card>
          <Card className="card-dark p-4">
            <p className="text-sm text-slate-400">Valor (Custo)</p>
            <p className="text-2xl font-bold text-white">
              €{(sessionStatus?.total_cost_value || 0).toFixed(2)}
            </p>
          </Card>
          <Card className="card-dark p-4">
            <p className="text-sm text-slate-400">Valor (Venda)</p>
            <p className="text-2xl font-bold text-emerald-400">
              €{(sessionStatus?.total_sell_value || 0).toFixed(2)}
            </p>
          </Card>
        </div>

        <Card className="card-dark flex-1 overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">Produtos Contados</CardTitle>
          </CardHeader>
          <ScrollArea className="h-[calc(100%-60px)]">
            <Table className="table-dark">
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Esperado</TableHead>
                  <TableHead className="text-right">Contagem</TableHead>
                  <TableHead className="text-right">Dif.</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sessionStatus?.counts || []).map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{item.product_name}</p>
                        {item.barcode && <p className="text-xs text-slate-500">{item.barcode}</p>}
                        {!!item.batches_counted?.length && (
                          <div className="mt-1 space-y-1">
                            {item.batches_counted.map((b, i) => (
                              <p key={i} className="text-xs text-slate-500">
                                Lote: {b.lot_number || 's/lote'} | Validade: {b.expiry_date || 's/validade'} | Contado: {b.counted_quantity}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-600">
                        {item.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">
                      {item.expected_quantity}
                    </TableCell>
                    <TableCell className="text-right font-bold text-emerald-400">
                      {item.counted_quantity}
                    </TableCell>
                    <TableCell className={`text-right font-semibold ${item.difference === 0 ? 'text-slate-300' : item.difference > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {item.difference > 0 ? `+${item.difference}` : item.difference}
                    </TableCell>
                    <TableCell className="text-right">
                      €{(item.sell_value || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </Card>

        <Dialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Finalizar Inventário</DialogTitle>
              <DialogDescription className="text-slate-400">
                Escolha como pretende finalizar a contagem
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400">Resumo da contagem:</p>
                <p className="text-white">
                  {sessionStatus?.products_counted || 0} produtos | {sessionStatus?.total_items_counted || 0} unidades
                </p>
                <p className="text-emerald-400">
                  Valor total: €{(sessionStatus?.total_sell_value || 0).toFixed(2)}
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => finalizeSession(true, false)}
                  className="w-full h-14 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="finalize-full-adjust"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Finalizar Completo (ajustar stock)
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Produtos não contados mantêm-se fora deste ajuste direto nesta fase
                </p>

                <Button
                  onClick={() => finalizeSession(true, true)}
                  variant="outline"
                  className="w-full h-14 border-slate-700"
                  data-testid="finalize-partial-adjust"
                >
                  Finalizar Parcial (ajustar apenas contados)
                </Button>

                <Button
                  onClick={() => finalizeSession(false, false)}
                  variant="outline"
                  className="w-full h-14 border-slate-700"
                  data-testid="finalize-no-adjust"
                >
                  Finalizar sem ajustar stock
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Inventário</h1>
          <p className="text-slate-400">Visão geral do stock, contagem e preparação do stock inicial</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button
            onClick={() => navigate('/stockmovel')}
            variant="outline"
            className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
            data-testid="open-stock-movel-btn"
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Stock Móvel
          </Button>
          <Button
            onClick={() => setShowInitialStartModal(true)}
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid="start-initial-inventory-btn"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Stock Inicial
          </Button>
          <Button
            onClick={() => setShowStartModal(true)}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="start-inventory-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Iniciar Contagem
          </Button>
        </div>
      </div>

      <Card className="card-dark border-amber-500/30">
        <div className="p-5 space-y-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-amber-300" />
                <h2 className="text-lg font-semibold text-white">Stock Inicial</h2>
                <Badge className="bg-amber-500/20 text-amber-200 border border-amber-500/30">
                  Implementação inicial
                </Badge>
              </div>
              <p className="text-sm text-slate-400">
                Use este modo para lançar o stock real por artigo, criando linhas por validade. Para artigos sem validade, marque a opção respetiva.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              {!initialSessionId ? (
                <Button
                  onClick={() => setShowInitialStartModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950"
                  data-testid="open-initial-session-btn"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Iniciar sessão de stock inicial
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowInitialFinalizeModal(true)}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid="open-finalize-initial-session-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Finalizar stock inicial
                </Button>
              )}
            </div>
          </div>

          {initialSessionId ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Sessão</p>
                  <p className="mt-1 font-semibold text-white">
                    {initialSessionStatus?.name || 'Stock inicial'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {initialSessionStatus?.products_counted || 0} artigos gravados
                  </p>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Linhas</p>
                  <p className="mt-1 font-semibold text-white">
                    {initialSessionStatus?.rows_counted || 0}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Cada validade conta como uma linha
                  </p>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Unidades</p>
                  <p className="mt-1 font-semibold text-emerald-400">
                    {initialSessionStatus?.total_units || 0}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Total já introduzido no stock inicial
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] gap-5">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ScanLine className="w-4 h-4 text-amber-300" />
                      <p className="font-medium text-white">Ler artigo</p>
                    </div>

                    <div className="flex flex-col gap-3 md:flex-row">
                      <div className="flex-1">
                        <Label className="text-slate-300">Código de barras ou QR atual</Label>
                        <Input
                          ref={initialScanInputRef}
                          value={initialScanInput}
                          onChange={(e) => setInitialScanInput(e.target.value)}
                          onKeyDown={handleInitialScanKeyDown}
                          placeholder="Leia com pistola ou introduza o código"
                          className="mt-2 bg-slate-800 border-slate-700"
                          data-testid="initial-scan-input"
                        />
                      </div>

                      <div className="md:self-end">
                        <Button
                          onClick={resolveInitialProductByScan}
                          className="w-full md:w-auto bg-amber-500 hover:bg-amber-600 text-slate-950"
                          data-testid="resolve-initial-product-btn"
                        >
                          <Search className="w-4 h-4 mr-2" />
                          Resolver artigo
                        </Button>
                      </div>
                    </div>
                  </div>

                    {initialResolvedProduct ? (
                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div>
                            <p className="font-semibold text-white">{initialResolvedProduct.product_name}</p>
                            <p className="text-sm text-slate-400">
                              {initialResolvedProduct.category || 'Sem categoria'}
                              {initialResolvedProduct.barcode ? ` • ${initialResolvedProduct.barcode}` : ''}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Ref: {initialResolvedProduct.reference || '-'}
                              {initialResolvedProduct.brand ? ` • Marca: ${initialResolvedProduct.brand}` : ''}
                            </p>
                          </div>
                          <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            Artigo resolvido
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                          <div>
                            <Label className="text-slate-300">Categoria</Label>
                            <select
                              value={initialResolvedProduct.category || 'Sem categoria'}
                              onChange={(e) =>
                                setInitialResolvedProduct((prev) => ({
                                  ...prev,
                                  category: e.target.value,
                                }))
                              }
                              className="mt-2 h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white outline-none"
                              data-testid="initial-product-category"
                            >
                              {[
                                ...new Set(
                                  ['Sem categoria', ...categories.filter((cat) => cat && cat !== 'all')]
                                ),
                              ].map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <Label className="text-slate-300">Custo</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={initialResolvedProduct.cost_price ?? 0}
                              onChange={(e) =>
                                setInitialResolvedProduct((prev) => ({
                                  ...prev,
                                  cost_price: e.target.value,
                                }))
                              }
                              className="mt-2 bg-slate-800 border-slate-700"
                              data-testid="initial-product-cost-price"
                            />
                          </div>

                          <div>
                            <Label className="text-slate-300">PVP</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={initialResolvedProduct.sell_price ?? 0}
                              onChange={(e) =>
                                setInitialResolvedProduct((prev) => ({
                                  ...prev,
                                  sell_price: e.target.value,
                                }))
                              }
                              className="mt-2 bg-slate-800 border-slate-700"
                              data-testid="initial-product-sell-price"
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                        {initialEntryRows.map((row, index) => (
                          <div key={index} className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-medium text-white">
                                Linha {index + 1}
                              </p>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => removeInitialEntryRow(index)}
                                className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                                disabled={initialEntryRows.length <= 1}
                                data-testid={`remove-initial-row-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                              <div>
                                <Label className="text-slate-300">Quantidade</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  inputMode="numeric"
                                  value={row.quantity}
                                  onChange={(e) => updateInitialEntryRow(index, 'quantity', e.target.value)}
                                  className="mt-2 bg-slate-800 border-slate-700"
                                  data-testid={`initial-row-quantity-${index}`}
                                />
                              </div>

                              <div className="md:col-span-2">
                                <Label className="text-slate-300">Validade</Label>
                                <Input
                                  type="date"
                                  min={expiryLimits.min}
                                  max={expiryLimits.max}
                                  value={row.expiry_date}
                                  onChange={(e) => updateInitialEntryRow(index, 'expiry_date', e.target.value)}
                                  disabled={row.no_expiry}
                                  title={`Escolha uma data entre ${expiryLimits.min} e ${expiryLimits.max}`}
                                  className="mt-2 bg-slate-800 border-slate-700"
                                  data-testid={`initial-row-expiry-${index}`}
                                />
                              </div>

                              <div>
                                <Label className="text-slate-300">Lote opcional</Label>
                                <Input
                                  value={row.lot_number}
                                  onChange={(e) => updateInitialEntryRow(index, 'lot_number', e.target.value)}
                                  placeholder="Automático se vazio"
                                  className="mt-2 bg-slate-800 border-slate-700"
                                  data-testid={`initial-row-lot-${index}`}
                                />
                              </div>
                            </div>

                            <div className="mt-3 flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                              <input
                                id={`no-expiry-${index}`}
                                type="checkbox"
                                checked={!!row.no_expiry}
                                onChange={(e) => updateInitialEntryRow(index, 'no_expiry', e.target.checked)}
                                className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500"
                              />
                              <Label htmlFor={`no-expiry-${index}`} className="text-sm text-slate-300 cursor-pointer">
                                Artigo sem validade
                              </Label>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addInitialEntryRow}
                          className="border-slate-700"
                          data-testid="add-initial-row-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Adicionar nova validade
                        </Button>

                        <Button
                          type="button"
                          onClick={saveInitialResolvedProduct}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid="save-initial-product-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Gravar artigo no stock inicial
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-6 text-center">
                      <ScanLine className="w-10 h-10 mx-auto text-slate-500 mb-3" />
                      <p className="text-white font-medium">Aguardando leitura de artigo</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Leia um código para abrir o formulário de quantidade e validades.
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarDays className="w-4 h-4 text-amber-300" />
                      <p className="font-medium text-white">Último registo</p>
                    </div>

                    {initialLastSave ? (
                      <div className="space-y-2">
                        <p className="font-semibold text-white">
                          {initialLastSave.product_name || 'Artigo gravado'}
                        </p>
                        <p className="text-sm text-slate-400">
                          {initialLastSave.rows_saved || 0} linha(s) | {initialLastSave.total_quantity || 0} unidade(s)
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">
                        Ainda sem registos nesta sessão.
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                    <p className="font-medium text-white mb-3">Artigos já preparados</p>

                    <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
                      {(initialSessionStatus?.entries || []).length > 0 ? (
                        initialSessionStatus.entries.map((entry, index) => (
                          <div key={`${entry.product_id || 'item'}-${index}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                            <p className="font-medium text-white">{entry.product_name}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {entry.category || 'Sem categoria'}
                              {entry.barcode ? ` • ${entry.barcode}` : ''}
                            </p>

                            <div className="mt-2 space-y-1">
                              {(entry.rows || []).map((row, rowIndex) => (
                                <p key={rowIndex} className="text-xs text-slate-400">
                                  Qtd: {row.quantity} | {row.no_expiry ? 'Sem validade' : `Validade: ${row.expiry_date || '-'}`} | Lote: {row.lot_number || 'automático'}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">
                          Ainda não existem artigos gravados nesta sessão.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-6">
              <p className="font-medium text-white">Ainda não existe sessão de stock inicial aberta.</p>
              <p className="text-sm text-slate-400 mt-1">
                Abra uma sessão para começar a ler artigos, lançar quantidades e separar por validade.
              </p>
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Produtos</p>
              <p className="text-2xl font-bold text-white">
                {stockOverview?.summary?.total_products || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Unidades</p>
              <p className="text-2xl font-bold text-white">
                {stockOverview?.summary?.total_units || 0}
              </p>
            </div>
          </div>
        </Card>

        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Valor Custo</p>
              <p className="text-2xl font-bold text-white">
                €{(stockOverview?.summary?.total_cost_value || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="card-dark p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Euro className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Valor Venda</p>
              <p className="text-2xl font-bold text-emerald-400">
                €{(stockOverview?.summary?.total_sell_value || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {stockOverview?.summary?.below_minimum_count > 0 && (
        <Card className="card-dark p-4 border-amber-500/50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <div>
              <p className="font-medium text-white">
                {stockOverview.summary.below_minimum_count} produtos abaixo do stock mínimo
              </p>
              <p className="text-sm text-slate-400">
                Verifique os produtos e faça nova encomenda
              </p>
            </div>
          </div>
        </Card>
      )}


      {stockOverview?.summary?.categories_summary?.length > 0 && (
        <Card className="card-dark p-6">
          <CardHeader className="p-0 pb-4">
            <CardTitle className="text-lg text-white">Resumo por Categoria</CardTitle>
          </CardHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stockOverview.summary.categories_summary.map((cat, idx) => (
              <div key={idx} className="p-4 bg-slate-800 rounded-lg">
                <p className="font-medium text-white mb-1">{cat.category}</p>
                <p className="text-sm text-slate-400">{cat.products} produtos | {cat.units} un</p>
                <p className="text-sm text-slate-400">Custo: €{(cat.cost_value || 0).toFixed(2)}</p>
                <p className="text-sm text-emerald-400">Venda: €{(cat.sell_value || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="card-dark p-6">
        <CardHeader className="p-0 pb-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg text-white">Filtros do Histórico</CardTitle>
            <Button
              type="button"
              variant="outline"
              onClick={clearHistoryFilters}
              className="border-slate-700"
              data-testid="history-clear-filters-btn"
            >
              Limpar filtros
            </Button>
          </div>
        </CardHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <div>
            <Label className="text-slate-300">Tipo</Label>
            <select
              value={historyTypeFilter}
              onChange={(e) => setHistoryTypeFilter(e.target.value)}
              className="mt-2 h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white outline-none"
              data-testid="history-type-filter"
            >
              <option value="all">Todos</option>
              <option value="initial">Stock inicial</option>
              <option value="normal">Contagem</option>
            </select>
          </div>
      
          <div>
            <Label className="text-slate-300">Sessão</Label>
            <select
              value={historySessionFilter}
              onChange={(e) => setHistorySessionFilter(e.target.value)}
              className="mt-2 h-10 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white outline-none"
              data-testid="history-session-filter"
            >
              <option value="all">Todas as sessões</option>
              {historySessionOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
      
          <div>
            <Label className="text-slate-300">Produto</Label>
            <Input
              value={historyProductFilter}
              onChange={(e) => setHistoryProductFilter(e.target.value)}
              placeholder="Nome, código, categoria, lote..."
              className="mt-2 bg-slate-800 border-slate-700"
              data-testid="history-product-filter"
            />
          </div>
      
          <div>
            <Label className="text-slate-300">Data início</Label>
            <Input
              type="date"
              value={historyDateFrom}
              onChange={(e) => setHistoryDateFrom(e.target.value)}
              className="mt-2 bg-slate-800 border-slate-700"
              data-testid="history-date-from"
            />
          </div>
      
          <div>
            <Label className="text-slate-300">Data fim</Label>
            <Input
              type="date"
              value={historyDateTo}
              onChange={(e) => setHistoryDateTo(e.target.value)}
              className="mt-2 bg-slate-800 border-slate-700"
              data-testid="history-date-to"
            />
          </div>
        </div>
      </Card>

      <Card className="card-dark p-6">
        <CardHeader className="p-0 pb-4">
          <CardTitle className="text-lg text-white">Resumo das Sessões de Inventário</CardTitle>
        </CardHeader>

        {filteredInventorySessionSummaries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredInventorySessionSummaries.map((session) => (
              <div
                key={session.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelectedHistorySession(session.id);
                  setShowHistorySessionModal(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedHistorySession(session.id);
                    setShowHistorySessionModal(true);
                  }
                }}
                className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-emerald-500/40 hover:bg-slate-900/80 cursor-pointer"
                data-testid={`history-session-card-${session.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-white">{session.name}</p>
                  <Badge className={session.type === 'initial' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/30' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'}>
                    {session.type === 'initial' ? 'Stock inicial' : 'Contagem'}
                  </Badge>
                </div>
        
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <p className="text-xs text-slate-500">
                    {session.completed_at || session.created_at || '-'}
                  </p>
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    {inventoryStatusLabel(session.status)}
                  </Badge>
                </div>
        
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Artigos</p>
                    <p className="text-white font-semibold">{session.products_counted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Linhas</p>
                    <p className="text-white font-semibold">{session.rows_counted}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Unidades</p>
                    <p className="text-emerald-400 font-semibold">{session.total_units}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Valor</p>
                    <p className="text-emerald-400 font-semibold">€{Number(session.total_value || 0).toFixed(2)}</p>
                  </div>
                </div>

                {session.status === 'in_progress' && (
                  <div className="mt-4">
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        resumeHistorySession(session);
                      }}
                      className={session.type === 'initial' ? 'w-full bg-amber-500 hover:bg-amber-600 text-slate-950' : 'w-full bg-emerald-500 hover:bg-emerald-600'}
                      data-testid={`resume-history-session-${session.id}`}
                    >
                      {session.type === 'initial' ? 'Retomar stock inicial' : 'Retomar contagem'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500">Ainda não existem sessões registadas.</p>
        )}
      </Card>

      <Card className="card-dark">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg">Linhas de Inventário</CardTitle>
        </CardHeader>

        <div className="px-6 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-xs text-slate-400">Linhas filtradas</p>
              <p className="text-white font-semibold mt-1">{filteredHistoryTotals.rows}</p>
            </div>
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-xs text-slate-400">Unidades filtradas</p>
              <p className="text-emerald-400 font-semibold mt-1">{filteredHistoryTotals.units}</p>
            </div>
            <div className="rounded-lg bg-slate-800 p-3">
              <p className="text-xs text-slate-400">Valor filtrado</p>
              <p className="text-emerald-400 font-semibold mt-1">€{Number(filteredHistoryTotals.sell_value || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>
        <ScrollArea className="max-h-[520px]">
          <Table className="table-dark">
            <TableHeader>
              <TableRow>
                <TableHead>Sessão</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Qtd</TableHead>
                <TableHead>Lote</TableHead>
                <TableHead>Validade</TableHead>
                <TableHead className="text-right">Custo</TableHead>
                <TableHead className="text-right">PVP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInventoryHistoryLines.length > 0 ? (
                filteredInventoryHistoryLines.map((row) => (
                  <TableRow
                    key={row.key}
                    className="cursor-pointer"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedHistorySession(row.session_id);
                        setShowHistorySessionModal(true);
                      }
                    }}
                    onClick={() => {
                      setSelectedHistorySession(row.session_id);
                      setShowHistorySessionModal(true);
                    }}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{row.session_name}</p>
                        <p className="text-xs text-slate-500">{row.created_at || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-600">
                        {row.session_type_label || row.session_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white">{row.product_name}</p>
                        {row.barcode ? <p className="text-xs text-slate-500">{row.barcode}</p> : null}
                      </div>
                    </TableCell>
                    <TableCell>{row.category || 'Sem categoria'}</TableCell>
                    <TableCell className="text-right font-semibold">{row.quantity}</TableCell>
                    <TableCell>{row.lot_number || '-'}</TableCell>
                    <TableCell>{row.expiry_date || '-'}</TableCell>
                    <TableCell className="text-right">€{Number(row.cost_price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-emerald-400">€{Number(row.sell_price || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    Ainda não existem linhas de inventário registadas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Pesquisar produto, código, referência, marca..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700"
            data-testid="search-inventory"
          />
        </div>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-white outline-none"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'Todas as categorias' : cat}
            </option>
          ))}
        </select>
      </div>

      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead>
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.product_name)} className="inline-flex items-center gap-2 text-left">
                  Produto <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead>
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.category)} className="inline-flex items-center gap-2 text-left">
                  Categoria <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.total_stock)} className="inline-flex items-center gap-2 ml-auto">
                  Quantidade <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.cost_price)} className="inline-flex items-center gap-2 ml-auto">
                  Custo Un. <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.sell_price)} className="inline-flex items-center gap-2 ml-auto">
                  PVP <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead className="text-right">
                <button type="button" onClick={() => handleSort(SORTABLE_COLUMNS.sell_value)} className="inline-flex items-center gap-2 ml-auto">
                  Valor Stock <ArrowUpDown className="w-4 h-4" />
                </button>
              </TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((item) => {
              const isBelowMinimum = (item.total_stock || 0) <= (item.min_stock || 0);

              return (
                <TableRow key={item.product_id} data-testid={`stock-row-${item.product_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">{item.product_name}</p>
                      {item.barcode && <p className="text-xs text-slate-500">{item.barcode}</p>}
                      {!!item.batches?.length && (
                        <p className="text-xs text-slate-500">
                          {item.batches.length} lote(s)
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-slate-600">
                      {item.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {item.total_stock}
                  </TableCell>
                  <TableCell className="text-right">
                    €{(item.cost_price || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right text-emerald-400">
                    €{(item.sell_price || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div>
                      <p className="text-slate-400 text-xs">Custo: €{(item.cost_value || 0).toFixed(2)}</p>
                      <p className="text-emerald-400">Venda: €{(item.sell_value || 0).toFixed(2)}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {isBelowMinimum ? (
                      <Badge className="badge-warning">Stock Baixo</Badge>
                    ) : (
                      <Badge className="badge-success">OK</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum produto encontrado</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>



      <Dialog
        open={showHistorySessionModal}
        onOpenChange={(open) => {
          setShowHistorySessionModal(open);
          if (!open) setSelectedHistorySession(null);
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-800 max-w-5xl" data-testid="history-session-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-white">Detalhe da Sessão de Inventário</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedHistorySessionDetail ? `${selectedHistorySessionDetail.name} • ${selectedHistorySessionDetail.type_label}` : 'Sessão não encontrada'}
            </DialogDescription>
          </DialogHeader>
      
          {selectedHistorySessionDetail ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-lg font-semibold text-white">{selectedHistorySessionDetail.name}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge className={selectedHistorySessionDetail.type === 'initial' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/30' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'}>
                        {selectedHistorySessionDetail.type_label}
                      </Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300">
                        Estado: {inventoryStatusLabel(selectedHistorySessionDetail.status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-3">
                      Data: {selectedHistorySessionDetail.completed_at || selectedHistorySessionDetail.created_at || '-'}
                    </p>
                  </div>
                </div>
              </div>
          
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg bg-slate-800 p-4">
                  <p className="text-xs text-slate-400">Artigos</p>
                  <p className="text-white font-semibold mt-1">{selectedHistorySessionDetail.products_counted}</p>
                </div>
                <div className="rounded-lg bg-slate-800 p-4">
                  <p className="text-xs text-slate-400">Linhas</p>
                  <p className="text-white font-semibold mt-1">{selectedHistorySessionDetail.rows_counted}</p>
                </div>
                <div className="rounded-lg bg-slate-800 p-4">
                  <p className="text-xs text-slate-400">Unidades</p>
                  <p className="text-emerald-400 font-semibold mt-1">{selectedHistorySessionDetail.total_units}</p>
                </div>
                <div className="rounded-lg bg-slate-800 p-4">
                  <p className="text-xs text-slate-400">Valor</p>
                  <p className="text-emerald-400 font-semibold mt-1">€{Number(selectedHistorySessionDetail.total_value || 0).toFixed(2)}</p>
                </div>
              </div>
              <ScrollArea className="max-h-[420px]">
                <Table className="table-dark">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead>Validade</TableHead>
                      <TableHead className="text-right">Custo</TableHead>
                      <TableHead className="text-right">PVP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedHistorySessionLines.length > 0 ? (
                      selectedHistorySessionLines.map((row) => (
                        <TableRow key={`detail-${row.key}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-white">{row.product_name}</p>
                              {row.barcode ? <p className="text-xs text-slate-500">{row.barcode}</p> : null}
                            </div>
                          </TableCell>
                          <TableCell>{row.category || 'Sem categoria'}</TableCell>
                          <TableCell className="text-right font-semibold">{row.quantity}</TableCell>
                          <TableCell>{row.lot_number || '-'}</TableCell>
                          <TableCell>{row.expiry_date || '-'}</TableCell>
                          <TableCell className="text-right">€{Number(row.cost_price || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right text-emerald-400">€{Number(row.sell_price || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          Esta sessão não tem linhas para mostrar.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          ) : (
            <p className="text-slate-500">Sessão não encontrada.</p>
          )}
      
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowHistorySessionModal(false)}
              className="border-slate-700"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Iniciar Contagem de Inventário</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome da Sessão</Label>
              <Input
                placeholder="Ex: Inventário Mensal Fevereiro"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                className="bg-slate-800 border-slate-700"
                data-testid="session-name-input"
              />
            </div>

            <div className="p-4 bg-slate-800 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Instruções:</p>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• Use pistola ou câmara para ler códigos</li>
                <li>• Cada leitura conta 1 unidade</li>
                <li>• Se ler QR de lote, fica associado o lote e a validade</li>
                <li>• Pode anular a última leitura</li>
                <li>• Finalize total, parcial, ou sem ajuste</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={startCountingSession} className="bg-emerald-500 hover:bg-emerald-600" data-testid="confirm-start-btn">
              <Play className="w-4 h-4 mr-2" />
              Iniciar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInitialStartModal} onOpenChange={setShowInitialStartModal}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Iniciar sessão de stock inicial</DialogTitle>
            <DialogDescription className="text-slate-400">
              Esta sessão serve para lançar o stock real artigo a artigo, com linhas separadas por validade ou sem validade.
            </DialogDescription>
          </DialogHeader>
      
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome da Sessão</Label>
              <Input
                placeholder="Ex: Stock inicial loja abril"
                value={initialSessionName}
                onChange={(e) => setInitialSessionName(e.target.value)}
                className="bg-slate-800 border-slate-700"
                data-testid="initial-session-name-input"
              />
            </div>
      
            <div>
              <Label className="text-slate-300">Notas</Label>
              <Input
                placeholder="Ex: prateleiras A a F, balcão, armazém"
                value={initialSessionNotes}
                onChange={(e) => setInitialSessionNotes(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
      
            <div className="p-4 bg-slate-800 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Regras desta fase:</p>
              <ul className="text-sm text-slate-300 space-y-1">
                <li>• Ler código de barras ou QR do artigo</li>
                <li>• Indicar quantidade por validade</li>
                <li>• Para artigos não perecíveis, marcar “artigo sem validade”</li>
                <li>• O lote pode ser automático se não existir lote físico disponível</li>
                <li>• No fim, o sistema cria os lotes iniciais e atualiza o stock</li>
              </ul>
            </div>
          </div>
      
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInitialStartModal(false)}
              className="border-slate-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={startInitialInventorySession}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              data-testid="confirm-start-initial-session-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Iniciar sessão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInitialFinalizeModal} onOpenChange={setShowInitialFinalizeModal}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Finalizar stock inicial</DialogTitle>
            <DialogDescription className="text-slate-400">
              Esta ação vai aplicar no stock os artigos preparados nesta sessão inicial.
            </DialogDescription>
          </DialogHeader>
      
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-slate-800 p-4">
              <p className="text-sm text-slate-400">Resumo</p>
              <p className="text-white mt-1">
                {initialSessionStatus?.products_counted || 0} artigos | {initialSessionStatus?.rows_counted || 0} linhas | {initialSessionStatus?.total_units || 0} unidades
              </p>
            </div>
      
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-sm text-amber-100">
                Recomendado nesta primeira implementação: substituir os lotes existentes dos artigos lançados nesta sessão, para arrancar com uma base limpa e coerente.
              </p>
            </div>
          </div>
      
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInitialFinalizeModal(false)}
              className="border-slate-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => finalizeInitialInventorySession(true)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              data-testid="confirm-finalize-initial-session-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Finalizar stock inicial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
